import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Edit, Trash2, Package, Camera, Upload, X, Grid, List, Shuffle } from 'lucide-react';
import { toast } from 'sonner';
import { optimizeImage, formatFileSize, validateImage } from '@/utils/imageOptimizer';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [stockFilter, setStockFilter] = useState('all'); // 'all', 'low', 'out'
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [imageOptimizing, setImageOptimizing] = useState(false);
  const [imageInfo, setImageInfo] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category_id: '',
    sale_price: '',
    min_sale_price: '',
    stock: '',
    expiry_date: '',
    image: '',
  });

  const [editingCategory, setEditingCategory] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    let filtered = products;
    
    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.code.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply stock filter
    if (stockFilter === 'low') {
      filtered = filtered.filter(p => p.stock > 0 && p.stock <= 5);
    } else if (stockFilter === 'out') {
      filtered = filtered.filter(p => p.stock === 0);
    }
    
    setFilteredProducts(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [searchQuery, stockFilter, products]);

  // Calculate pagination
  const totalPages = itemsPerPage === 'all' 
    ? 1 
    : Math.ceil(filteredProducts.length / itemsPerPage);
  
  const paginatedProducts = itemsPerPage === 'all' 
    ? filteredProducts 
    : filteredProducts.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === 'all' ? 'all' : parseInt(value));
    setCurrentPage(1);
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Error al cargar categorías');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    
    try {
      if (editingCategory) {
        await axios.put(`/categories/${editingCategory.id}`, { name: newCategory });
        toast.success('Categoría actualizada');
        setEditingCategory(null);
      } else {
        await axios.post('/categories', { name: newCategory });
        toast.success('Categoría agregada');
      }
      setNewCategory('');
      // setIsCategoryDialogOpen(false); // Keep dialog open
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar categoría');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategory(category.name);
  };

  const handleCancelEditCategory = () => {
    setEditingCategory(null);
    setNewCategory('');
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta categoría?')) return;

    try {
      await axios.delete(`/categories/${categoryId}`);
      toast.success('Categoría eliminada');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar categoría');
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar imagen
    const validation = validateImage(file, {
      maxSize: 10 * 1024 * 1024, // 10MB antes de optimizar
      allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    });

    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    try {
      setImageOptimizing(true);
      const originalSize = file.size;

      // Optimizar imagen
      const optimizedFile = await optimizeImage(file, {
        maxWidth: 800,
        maxHeight: 800,
        quality: 0.85,
        maintainAspectRatio: true
      });

      const optimizedSize = optimizedFile.size;

      // Mostrar información de optimización
      setImageInfo({
        originalSize: formatFileSize(originalSize),
        optimizedSize: formatFileSize(optimizedSize),
        reduction: ((originalSize - optimizedSize) / originalSize * 100).toFixed(1)
      });

      // Convertir a base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setFormData({ ...formData, image: base64String });
        setImagePreview(base64String);
        
        // Mostrar mensaje de éxito
        if (optimizedSize < originalSize) {
          toast.success(
            `Imagen optimizada: ${formatFileSize(originalSize)} → ${formatFileSize(optimizedSize)} (${((originalSize - optimizedSize) / originalSize * 100).toFixed(1)}% reducido)`
          );
        }
      };
      reader.readAsDataURL(optimizedFile);
    } catch (error) {
      console.error('Error al optimizar imagen:', error);
      toast.error('Error al procesar la imagen');
    } finally {
      setImageOptimizing(false);
    }
  };

  const generateProductCode = async () => {
    try {
      const response = await axios.get('/products/generate-code');
      setFormData({ ...formData, code: response.data.code });
      toast.success('Código generado automáticamente');
    } catch (error) {
      toast.error('Error al generar código');
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, image: '' });
    setImagePreview('');
    setImageInfo(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        sale_price: parseFloat(formData.sale_price),
        min_sale_price: parseFloat(formData.min_sale_price),
        stock: parseInt(formData.stock),
      };

      if (editingProduct) {
        await axios.put(`/products/${editingProduct.id}`, data);
        toast.success('Producto actualizado');
      } else {
        await axios.post('/products', data);
        toast.success('Producto agregado');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setIsDetailModalOpen(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      code: product.code,
      name: product.name,
      description: product.description || '',
      category_id: product.category_id,
      sale_price: product.sale_price.toString(),
      min_sale_price: product.min_sale_price.toString(),
      stock: product.stock.toString(),
      expiry_date: product.expiry_date || '',
      image: product.image || '',
    });
    setImagePreview(product.image || '');
    setIsDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;

    try {
      await axios.delete(`/products/${productId}`);
      toast.success('Producto eliminado');
      fetchProducts();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      category_id: '',
      sale_price: '',
      min_sale_price: '',
      stock: '',
      expiry_date: '',
      image: '',
    });
    setEditingProduct(null);
    setImagePreview('');
    setImageInfo(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Sin categoría';
  };

  return (
    <div className="products-page" data-testid="products-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Productos</h1>
          <p className="page-subtitle">Gestiona el inventario de productos</p>
        </div>
        <div className="header-actions">
          <Button 
            variant="outline" 
            onClick={() => setIsCategoryDialogOpen(true)}
            data-testid="add-category-btn"
          >
            Categorías
          </Button>
          <Button 
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            data-testid="add-product-btn"
          >
            <Plus size={20} />
            Agregar Producto
          </Button>
        </div>
      </div>

      <Card className="search-card">
        <div className="search-controls">
          <div className="search-box">
            <Search size={20} className="search-icon" />
            <Input
              type="text"
              placeholder="Buscar por nombre o código..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              data-testid="search-products-input"
            />
          </div>
          <div className="view-toggle">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              data-testid="grid-view-btn"
            >
              <Grid size={18} />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              data-testid="list-view-btn"
            >
              <List size={18} />
            </Button>
          </div>
        </div>
      </Card>

      {/* Stock Filters */}
      <div className="stock-filters">
        <Button
          variant={stockFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStockFilter('all')}
          data-testid="filter-all-btn"
        >
          Todos ({products.length})
        </Button>
        <Button
          variant={stockFilter === 'low' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStockFilter('low')}
          className="filter-low-stock"
          data-testid="filter-low-stock-btn"
        >
          <Package size={16} />
          Stock Bajo ({products.filter(p => p.stock > 0 && p.stock <= 5).length})
        </Button>
        <Button
          variant={stockFilter === 'out' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStockFilter('out')}
          className="filter-out-stock"
          data-testid="filter-out-stock-btn"
        >
          Sin Stock ({products.filter(p => p.stock === 0).length})
        </Button>
      </div>

      {viewMode === 'grid' ? (
        <div className="products-grid">
          {paginatedProducts.map((product) => (
            <Card 
              key={product.id} 
              className="product-card" 
              data-testid={`product-card-${product.id}`}
              onClick={() => handleViewDetails(product)}
              style={{ cursor: 'pointer' }}
            >
            <div className="product-image">
              {product.image ? (
                <img src={product.image} alt={product.name} />
              ) : (
                <div className="product-placeholder">
                  <Package size={40} />
                </div>
              )}
            </div>
            
            <div className="product-info">
              <div className="product-header">
                <h3 className="product-name">{product.name}</h3>
                <span className="product-code">{product.code}</span>
              </div>
              
              <div className="product-category">
                {getCategoryName(product.category_id)}
              </div>
              
              <div className="product-prices">
                <div className="price-item">
                  <span className="price-label">Venta:</span>
                  <span className="price-value">${product.sale_price.toFixed(2)}</span>
                </div>
                <div className="price-item">
                  <span className="price-label">Stock:</span>
                  <span className={`stock-badge ${product.stock <= 5 ? 'low-stock' : ''}`}>
                    {product.stock}
                  </span>
                </div>
              </div>

              {product.expiry_date && (
                <div className="product-expiry">
                  Vence: {new Date(product.expiry_date).toLocaleDateString()}
                </div>
              )}
            </div>

            <div className="product-actions">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(product);
                }}
                data-testid={`edit-product-${product.id}`}
              >
                <Edit size={16} />
              </Button>
              <Button 
                size="sm" 
                variant="destructive" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(product.id);
                }}
                data-testid={`delete-product-${product.id}`}
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </Card>
        ))}
        </div>
      ) : (
        <Card className="table-card">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Imagen</th>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Categoría</th>
                  <th>Precio</th>
                  <th>Stock</th>
                  <th>Vencimiento</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProducts.map((product) => (
                  <tr key={product.id} data-testid={`product-row-${product.id}`}>
                    <td>
                      <div className="table-image">
                        {product.image ? (
                          <img src={product.image} alt={product.name} />
                        ) : (
                          <Package size={24} />
                        )}
                      </div>
                    </td>
                    <td><span className="table-code">{product.code}</span></td>
                    <td><strong>{product.name}</strong></td>
                    <td><span className="table-category">{getCategoryName(product.category_id)}</span></td>
                    <td><span className="table-price">${product.sale_price.toFixed(2)}</span></td>
                    <td>
                      <span className={`table-stock ${product.stock <= 5 ? 'low' : ''}`}>
                        {product.stock}
                      </span>
                    </td>
                    <td>{product.expiry_date ? new Date(product.expiry_date).toLocaleDateString() : '-'}</td>
                    <td>
                      <div className="table-actions">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(product)}>
                          <Edit size={14} />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(product.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination Controls */}
      {filteredProducts.length > 0 && (
        <Card className="pagination-card">
          <div className="pagination-controls">
            <div className="items-per-page">
              <Label htmlFor="items-per-page">Elementos por página:</Label>
              <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                <SelectTrigger className="items-per-page-select" data-testid="items-per-page-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {itemsPerPage !== 'all' && (
              <div className="pagination-info">
                <span>
                  Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredProducts.length)} de {filteredProducts.length}
                </span>
              </div>
            )}

            {itemsPerPage !== 'all' && totalPages > 1 && (
              <div className="pagination-buttons">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  data-testid="prev-page-btn"
                >
                  Anterior
                </Button>

                <div className="page-numbers">
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    // Show first, last, current, and adjacent pages
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                          data-testid={`page-${page}-btn`}
                        >
                          {page}
                        </Button>
                      );
                    } else if (
                      page === currentPage - 2 ||
                      page === currentPage + 2
                    ) {
                      return <span key={page} className="page-ellipsis">...</span>;
                    }
                    return null;
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  data-testid="next-page-btn"
                >
                  Siguiente
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {filteredProducts.length === 0 && (
        <div className="empty-state">
          <Package size={64} />
          <h3>No hay productos</h3>
          <p>Agrega tu primer producto para comenzar</p>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="product-form">
            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="code">Código*</Label>
                <div className="code-input-group">
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    required
                    data-testid="product-code-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateProductCode}
                    className="generate-code-btn"
                    data-testid="generate-code-btn"
                    title="Generar código automático"
                  >
                    <Shuffle size={18} />
                  </Button>
                </div>
              </div>
              <div className="form-group">
                <Label htmlFor="name">Nombre*</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="product-name-input"
                />
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Información adicional sobre el producto..."
                rows={3}
                data-testid="product-description-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="category">Categoría*</Label>
              <Select value={formData.category_id} onValueChange={(value) => setFormData({...formData, category_id: value})}>
                <SelectTrigger data-testid="product-category-select">
                  <SelectValue placeholder="Selecciona una categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="sale_price">Precio Venta*</Label>
                <Input
                  id="sale_price"
                  type="number"
                  step="0.01"
                  value={formData.sale_price}
                  onChange={(e) => setFormData({...formData, sale_price: e.target.value})}
                  required
                  data-testid="product-sale-price-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="min_sale_price">Precio Mínimo* (Mínimo 10% sobre el costo)</Label>
                <Input
                  id="min_sale_price"
                  type="number"
                  step="0.01"
                  value={formData.min_sale_price}
                  onChange={(e) => setFormData({...formData, min_sale_price: e.target.value})}
                  required
                  data-testid="product-min-price-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="stock">Stock*</Label>
                <Input
                  id="stock"
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({...formData, stock: e.target.value})}
                  required
                  data-testid="product-stock-input"
                />
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="expiry_date">Fecha de Vencimiento</Label>
              <Input
                id="expiry_date"
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                data-testid="product-expiry-input"
              />
            </div>

            <div className="form-group">
              <Label>Imagen del Producto</Label>
              
              {imageOptimizing && (
                <div className="image-optimizing">
                  <div className="spinner"></div>
                  <p>Optimizando imagen...</p>
                </div>
              )}
              
              {imagePreview ? (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                  {imageInfo && (
                    <div className="image-info">
                      <p className="image-info-text">
                        Original: {imageInfo.originalSize} → Optimizada: {imageInfo.optimizedSize}
                        <span className="reduction-badge">-{imageInfo.reduction}%</span>
                      </p>
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={handleRemoveImage}
                    className="remove-image-btn"
                    data-testid="remove-image-btn"
                  >
                    <X size={16} />
                    Eliminar Imagen
                  </Button>
                </div>
              ) : (
                <div className="image-upload-buttons">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                    data-testid="file-input"
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                    data-testid="camera-input"
                  />
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="upload-btn"
                    data-testid="gallery-btn"
                  >
                    <Upload size={18} />
                    Desde Galería
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => cameraInputRef.current?.click()}
                    className="camera-btn"
                    data-testid="camera-btn"
                  >
                    <Camera size={18} />
                    Tomar Foto
                  </Button>
                </div>
              )}
              <p className="image-hint">Tamaño máximo: 2MB. Formatos: JPG, PNG, WebP</p>
            </div>

            <div className="form-actions">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-product-btn">
                {loading ? 'Guardando...' : (editingProduct ? 'Actualizar' : 'Guardar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isCategoryDialogOpen} onOpenChange={(open) => {
        setIsCategoryDialogOpen(open);
        if (!open) handleCancelEditCategory();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Editar Categoría' : 'Gestionar Categorías'}</DialogTitle>
          </DialogHeader>
          <div className="category-form">
            <Label htmlFor="category-name">Nombre de la Categoría</Label>
            <div className="flex gap-2">
              <Input
                id="category-name"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Ej: Suplementos"
                data-testid="category-name-input"
              />
              <Button onClick={handleAddCategory} data-testid="save-category-btn">
                {editingCategory ? 'Actualizar' : 'Agregar'}
              </Button>
              {editingCategory && (
                <Button variant="outline" onClick={handleCancelEditCategory}>
                  Cancelar
                </Button>
              )}
            </div>
            
            <div className="category-list">
              <h4>Categorías Existentes:</h4>
              <div className="category-items">
                {categories.map(cat => (
                  <div key={cat.id} className="category-item">
                    <span>{cat.name}</span>
                    <div className="category-actions">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleEditCategory(cat)}
                        title="Editar"
                      >
                        <Edit size={14} />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteCategory(cat.id)}
                        title="Eliminar"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="form-actions">
              <Button variant="outline" onClick={() => setIsCategoryDialogOpen(false)}>
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Detail Modal - Read Only */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="product-detail-modal">
          <DialogHeader>
            <DialogTitle>Detalles del Producto</DialogTitle>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="product-detail-content">
              {/* Image Section */}
              {selectedProduct.image && (
                <div className="detail-image-container">
                  <img 
                    src={selectedProduct.image} 
                    alt={selectedProduct.name}
                    className="detail-image"
                  />
                </div>
              )}

              {/* Info Grid */}
              <div className="detail-info-grid">
                <div className="detail-info-item">
                  <span className="detail-label">Nombre:</span>
                  <span className="detail-value">{selectedProduct.name}</span>
                </div>

                <div className="detail-info-item">
                  <span className="detail-label">Código:</span>
                  <span className="detail-value detail-code">{selectedProduct.code}</span>
                </div>

                <div className="detail-info-item">
                  <span className="detail-label">Categoría:</span>
                  <span className="detail-value">
                    {categories.find(c => c.id === selectedProduct.category_id)?.name || 'Sin categoría'}
                  </span>
                </div>

                <div className="detail-info-item">
                  <span className="detail-label">Precio de Venta:</span>
                  <span className="detail-value detail-price">${selectedProduct.sale_price.toFixed(2)}</span>
                </div>

                <div className="detail-info-item">
                  <span className="detail-label">Precio Mínimo:</span>
                  <span className="detail-value">${selectedProduct.min_sale_price.toFixed(2)}</span>
                </div>

                <div className="detail-info-item">
                  <span className="detail-label">Stock Disponible:</span>
                  <span className={`detail-value detail-stock ${selectedProduct.stock <= 5 ? 'low-stock' : ''}`}>
                    {selectedProduct.stock} unidades
                  </span>
                </div>

                {selectedProduct.expiry_date && (
                  <div className="detail-info-item">
                    <span className="detail-label">Fecha de Vencimiento:</span>
                    <span className="detail-value">
                      {new Date(selectedProduct.expiry_date).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {selectedProduct.description && (
                  <div className="detail-info-item full-width">
                    <span className="detail-label">Descripción:</span>
                    <p className="detail-description">{selectedProduct.description}</p>
                  </div>
                )}
              </div>

              {/* Close Button */}
              <div className="detail-actions">
                <Button 
                  variant="outline" 
                  onClick={() => setIsDetailModalOpen(false)}
                  className="close-detail-btn"
                >
                  Cerrar
                </Button>
                <Button 
                  onClick={(e) => {
                    setIsDetailModalOpen(false);
                    handleEdit(selectedProduct);
                  }}
                  data-testid="edit-from-detail-btn"
                >
                  <Edit size={16} />
                  Editar Producto
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .products-page {
          max-width: 1400px;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .page-subtitle {
          color: #64748b;
        }

        .header-actions {
          display: flex;
          gap: 1rem;
        }

        .search-card {
          padding: 1rem;
          margin-bottom: 2rem;
        }

        .search-controls {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
          flex: 1;
        }

        .view-toggle {
          display: flex;
          gap: 0.5rem;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          color: #64748b;
        }

        .search-input {
          padding-left: 3rem;
        }

        .stock-filters {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .stock-filters button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .filter-low-stock[data-variant="default"] {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          border-color: #f59e0b;
        }

        .filter-low-stock[data-variant="default"]:hover {
          background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
        }

        .filter-out-stock[data-variant="default"] {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          border-color: #ef4444;
        }

        .filter-out-stock[data-variant="default"]:hover {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.5rem;
        }

        .product-card {
          overflow: hidden;
          transition: all 0.3s;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .product-card:hover {
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }

        /* Product Detail Modal Styles */
        .product-detail-modal {
          max-width: 700px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .product-detail-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          padding: 0.5rem 0;
        }

        .detail-image-container {
          width: 100%;
          max-height: 400px;
          background: #f8fafc;
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e2e8f0;
        }

        .detail-image {
          max-width: 100%;
          max-height: 400px;
          object-fit: contain;
          display: block;
        }

        .detail-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.25rem;
        }

        .detail-info-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .detail-info-item.full-width {
          grid-column: 1 / -1;
        }

        .detail-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .detail-value {
          font-size: 1rem;
          font-weight: 500;
          color: #1e293b;
        }

        .detail-code {
          font-family: monospace;
          background: #f1f5f9;
          padding: 0.5rem;
          border-radius: 6px;
          font-size: 0.9375rem;
        }

        .detail-price {
          font-size: 1.5rem;
          font-weight: 700;
          color: #10b981;
        }

        .detail-stock {
          font-weight: 600;
        }

        .detail-stock.low-stock {
          color: #ef4444;
        }

        .detail-description {
          margin: 0;
          line-height: 1.6;
          color: #475569;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 8px;
          border-left: 3px solid #3b82f6;
        }

        .detail-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .close-detail-btn {
          min-width: 100px;
        }

        .product-image {
          width: 100%;
          height: 180px;
          background: #f1f5f9;
          overflow: hidden;
        }

        .product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .product-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
        }

        .product-info {
          padding: 1.25rem;
        }

        .product-header {
          margin-bottom: 0.75rem;
        }

        .product-name {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .product-code {
          font-size: 0.875rem;
          color: #64748b;
          font-family: monospace;
        }

        .product-category {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
        }

        .product-prices {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5rem;
        }

        .price-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .price-label {
          font-size: 0.875rem;
          color: #64748b;
        }

        .price-value {
          font-size: 1.125rem;
          font-weight: 700;
          color: #10b981;
        }

        .stock-badge {
          padding: 0.25rem 0.75rem;
          background: #d1fae5;
          color: #065f46;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 700;
        }

        .stock-badge.low-stock {
          background: #fee2e2;
          color: #991b1b;
        }

        .product-expiry {
          font-size: 0.875rem;
          color: #64748b;
        }

        .product-actions {
          display: flex;
          gap: 0.5rem;
          padding: 1rem 1.25rem;
          border-top: 1px solid #e2e8f0;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #94a3b8;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.5rem;
          color: #64748b;
        }

        .dialog-content {
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .product-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          margin-top: 1rem;
          padding-bottom: 1rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .code-input-group {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .code-input-group input {
          flex: 1;
        }

        .generate-code-btn {
          height: 40px;
          padding: 0 1rem;
          flex-shrink: 0;
        }

        .generate-code-btn:hover {
          background: #f8fafc;
          color: #10b981;
          border-color: #10b981;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          margin-top: 1rem;
        }

        .category-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .category-list {
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
          max-height: 300px;
          overflow-y: auto;
        }

        .category-items {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .category-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
        }

        .category-actions {
          display: flex;
          gap: 0.25rem;
        }

        .category-list h4 {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          margin-bottom: 0.75rem;
        }

        .image-preview-container {
          position: relative;
          width: 100%;
          max-width: 300px;
          margin-bottom: 1rem;
        }

        .image-preview {
          width: 100%;
          height: 200px;
          object-fit: cover;
          border-radius: 12px;
          border: 2px solid #e2e8f0;
        }

        .remove-image-btn {
          margin-top: 0.75rem;
          width: 100%;
        }

        .image-optimizing {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.5rem;
          background: #f8fafc;
          border: 2px dashed #cbd5e1;
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .spinner {
          width: 24px;
          height: 24px;
          border: 3px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .image-info {
          margin-top: 0.5rem;
          padding: 0.75rem;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 6px;
        }

        .image-info-text {
          font-size: 0.875rem;
          color: #166534;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .reduction-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          background: #22c55e;
          color: white;
          border-radius: 4px;
          font-weight: 600;
          font-size: 0.75rem;
        }

        .image-upload-buttons {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .upload-btn,
        .camera-btn {
          flex: 1;
          min-width: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 1rem;
          height: auto;
        }

        .camera-btn {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border: none;
        }

        .camera-btn:hover {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
        }

        .image-hint {
          font-size: 0.75rem;
          color: #64748b;
          margin-top: 0.5rem;
        }

        .table-card {
          padding: 0;
          overflow: hidden;
        }

        .table-container {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table thead {
          background: #f8fafc;
        }

        .data-table th {
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          font-size: 0.875rem;
          color: #64748b;
          border-bottom: 2px solid #e2e8f0;
          white-space: nowrap;
        }

        .data-table td {
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.9375rem;
        }

        .data-table tbody tr {
          transition: background 0.2s;
        }

        .data-table tbody tr:hover {
          background: #f8fafc;
        }

        .table-image {
          width: 50px;
          height: 50px;
          border-radius: 8px;
          overflow: hidden;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .table-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .table-code {
          font-family: monospace;
          color: #64748b;
          font-size: 0.875rem;
        }

        .table-category {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .table-price {
          color: #10b981;
          font-weight: 700;
        }

        .table-stock {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #d1fae5;
          color: #065f46;
          border-radius: 6px;
          font-weight: 700;
          font-size: 0.875rem;
        }

        .table-stock.low {
          background: #fee2e2;
          color: #991b1b;
        }

        .table-actions {
          display: flex;
          gap: 0.5rem;
        }

        .pagination-card {
          margin-top: 2rem;
          padding: 1.5rem;
        }

        .pagination-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .items-per-page {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .items-per-page-select {
          width: 120px;
        }

        .pagination-info {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .pagination-buttons {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .page-numbers {
          display: flex;
          gap: 0.25rem;
        }

        .page-ellipsis {
          display: flex;
          align-items: center;
          padding: 0 0.5rem;
          color: #64748b;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
          }

          .products-grid {
            grid-template-columns: 1fr;
          }

          .detail-info-grid {
            grid-template-columns: 1fr;
          }

          .detail-image-container {
            max-height: 300px;
          }

          .detail-actions {
            flex-direction: column;
          }

          .close-detail-btn {
            width: 100%;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .image-upload-buttons {
            flex-direction: column;
          }

          .upload-btn,
          .camera-btn {
            width: 100%;
          }

          .search-controls {
            flex-direction: column;
          }

          .view-toggle {
            width: 100%;
          }

          .view-toggle button {
            flex: 1;
          }

          .table-container {
            font-size: 0.875rem;
          }

          .data-table th,
          .data-table td {
            padding: 0.75rem 0.5rem;
          }

          .pagination-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .items-per-page {
            justify-content: space-between;
          }

          .pagination-buttons {
            flex-direction: column;
            width: 100%;
          }

          .page-numbers {
            justify-content: center;
            flex-wrap: wrap;
          }

          .pagination-info {
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Products;
